import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const steps = [
  {
    number: '01',
    title: 'Add Your Sources',
    icon: '◎',
    color: '#00d4ff',
    body: 'Go to Sources and add any RSS feed URL you want to follow. Not sure what to add? Head to the Discover tab — it has 42 community-curated feeds across Tech, News, Science, Crypto, Politics, and more. Free accounts get 3 sources. Premium gets unlimited.',
    rss: true,
  },
  {
    number: '02',
    title: 'Generate Your Brief',
    icon: '◈',
    color: '#7b61ff',
    body: 'Hit Generate Brief on the Brief tab. ANIMA pulls the 5 most recent articles from each of your sources — potentially 15, 30, even 50 articles — and synthesizes them into clean, readable summaries. What would take an hour to read yourself takes ANIMA seconds.',
    rss: false,
  },
  {
    number: '03',
    title: 'Read or Receive It',
    icon: '◉',
    color: '#00ffaa',
    body: 'Your brief appears instantly in the app. It\'s also delivered to your inbox automatically. Set your preferred delivery time in Profile. No algorithm deciding what matters. No ads. No engagement bait. Just your sources, synthesized.',
    rss: false,
  },
  {
    number: '04',
    title: 'Unlock ANIMA Analysis',
    icon: '⬡',
    color: '#ffd700',
    body: 'Premium users get ANIMA\'s cross-source analysis — she reads across all your summaries and surfaces patterns, contradictions, and insights you\'d miss reading each source in isolation. This is the feature that makes VERABRIEF genuinely different.',
    rss: false,
  },
];

const freeVsPremium = [
  { feature: 'RSS sources', free: '3', premium: 'Unlimited' },
  { feature: 'Daily briefs', free: '1/day', premium: 'Unlimited' },
  { feature: 'Email delivery', free: '✓', premium: '✓' },
  { feature: 'Source summaries', free: '✓', premium: '✓' },
  { feature: 'ANIMA cross-source analysis', free: '—', premium: '✓' },
  { feature: 'Discover tab voting', free: '✓', premium: '✓' },
  { feature: 'SMS delivery', free: '—', premium: 'Coming soon' },
  { feature: 'Weekly digest', free: '—', premium: 'Coming soon' },
  { feature: 'Source health monitoring', free: '—', premium: 'Coming soon' },
];

const AboutScreen = ({ user, onNavigate }) => {
  const navigate = useNavigate();
  const [rssOpen, setRssOpen] = useState(false);

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Hero */}
        <div style={styles.hero}>
          <div style={styles.heroEyebrow}>
            <span style={styles.eyebrowDot} />
            WHAT IS VERABRIEF
          </div>
          <h1 style={styles.heroTitle}>
            Your sources.<br />
            <span style={styles.heroAccent}>ANIMA's</span> synthesis.
          </h1>
          <p style={styles.heroSub}>
            VERABRIEF is a sovereign daily intelligence brief. You choose what to follow.
            ANIMA reads it, synthesizes it, and delivers it — no algorithm, no ads, no noise.
          </p>
          <div style={styles.heroBadges}>
            <span style={styles.badge}>◈ No ads ever</span>
            <span style={styles.badge}>◎ You own your feed</span>
            <span style={styles.badge}>⬡ ANIMA inside</span>
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* How it works */}
        <div style={styles.sectionHeader}>
          <span style={styles.sectionEyebrow}>HOW IT WORKS</span>
          <h2 style={styles.sectionTitle}>Four steps to sovereign intelligence</h2>
        </div>

        <div style={styles.steps}>
          {steps.map((step, i) => (
            <div key={i} style={styles.stepCard} className="fade-in">
              <div style={styles.stepLeft}>
                <div style={{ ...styles.stepNumber, color: step.color }}>{step.number}</div>
                <div style={{ ...styles.stepIcon, color: step.color, borderColor: `${step.color}30`, background: `${step.color}10` }}>
                  {step.icon}
                </div>
              </div>
              <div style={styles.stepRight}>
                <div style={styles.stepTitle}>{step.title}</div>
                <div style={styles.stepBody}>{step.body}</div>
                {step.rss && (
                  <button
                    style={styles.rssToggle}
                    onClick={() => setRssOpen(!rssOpen)}
                  >
                    {rssOpen ? '▲' : '▼'} What is an RSS feed?
                  </button>
                )}
                {step.rss && rssOpen && (
                  <div style={styles.rssExplainer}>
                    RSS (Really Simple Syndication) is a standard format that websites use to publish their latest content.
                    Think of it as a direct subscription to a site's content — no social media middleman, no algorithm filtering what you see.
                    Every major news outlet, blog, and publication has an RSS feed URL. It usually looks like{' '}
                    <span style={styles.rssCode}>https://example.com/feed.xml</span> or{' '}
                    <span style={styles.rssCode}>https://example.com/rss</span>.
                    VERABRIEF reads these feeds directly and ANIMA synthesizes them for you.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* The ANIMA difference */}
        <div style={styles.animaCard}>
          <div style={styles.animaHeader}>
            <span style={styles.animaBadge}>⬡ ANIMA INSIDE</span>
            <h2 style={styles.animaTitle}>Why ANIMA makes this different</h2>
          </div>
          <div style={styles.animaGrid}>
            <div style={styles.animaPoint}>
              <div style={styles.animaPointIcon}>◈</div>
              <div style={styles.animaPointTitle}>Cross-source synthesis</div>
              <div style={styles.animaPointBody}>ANIMA doesn't just summarize each source independently. She reads across all of them and finds patterns, contradictions, and emerging themes you'd miss reading them one at a time.</div>
            </div>
            <div style={styles.animaPoint}>
              <div style={styles.animaPointIcon}>◎</div>
              <div style={styles.animaPointTitle}>No filter bubble</div>
              <div style={styles.animaPointBody}>No engagement algorithm decides what you see. Your sources are your sources. ANIMA synthesizes what's there — including perspectives that challenge each other.</div>
            </div>
            <div style={styles.animaPoint}>
              <div style={styles.animaPointIcon}>◉</div>
              <div style={styles.animaPointTitle}>Sovereign by design</div>
              <div style={styles.animaPointBody}>VERABRIEF is built on ANIMACORE — a decentralized AI governance framework. Your data stays yours. No surveillance. No ad targeting. No manipulation.</div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Free vs Premium */}
        <div style={styles.sectionHeader}>
          <span style={styles.sectionEyebrow}>PRICING</span>
          <h2 style={styles.sectionTitle}>Free forever. Premium for power users.</h2>
          <p style={styles.sectionSub}>$4.95/month. Cancel anytime.</p>
        </div>

        <div style={styles.table}>
          <div style={styles.tableHeader}>
            <div style={styles.tableCol} />
            <div style={{ ...styles.tableColHeader, color: 'var(--text-secondary)' }}>FREE</div>
            <div style={{ ...styles.tableColHeader, color: '#00d4ff' }}>PREMIUM</div>
          </div>
          {freeVsPremium.map((row, i) => (
            <div key={i} style={{ ...styles.tableRow, background: i % 2 === 0 ? 'var(--surface)' : 'transparent' }}>
              <div style={styles.tableFeature}>{row.feature}</div>
              <div style={styles.tableValue}>{row.free}</div>
              <div style={{ ...styles.tableValue, color: row.premium === '—' ? 'var(--text-dim)' : '#00d4ff' }}>{row.premium}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        {!user?.premium && (
          <div style={styles.ctaCard}>
            <div style={styles.ctaTitle}>Ready to start?</div>
            <div style={styles.ctaSub}>Add your first source and generate your brief in under 2 minutes.</div>
            <div style={styles.ctaButtons}>
              <button
                className="btn btn-primary"
                style={styles.ctaBtn}
                onClick={() => navigate('/discover')}
              >
                Browse Discover Tab
              </button>
              <button
                className="btn btn-ghost"
                style={styles.ctaBtn}
                onClick={() => navigate('/sources')}
              >
                Add a Source
              </button>
            </div>
          </div>
        )}

        {/* Coming soon */}
        <div style={styles.comingSoon}>
          <div style={styles.comingSoonTitle}>On the horizon</div>
          <div style={styles.comingSoonGrid}>
            {['Weekly digest email', 'SMS morning delivery', 'Source health monitoring', 'Brief sharing', 'ANIMA source recommendations', 'VERAKNOX sovereign OS'].map((item, i) => (
              <div key={i} style={styles.comingSoonItem}>
                <span style={styles.comingSoonDot}>◇</span>
                {item}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    padding: '40px 24px',
  },
  container: {
    maxWidth: 780,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  heroEyebrow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: 'var(--text-dim)',
  },
  eyebrowDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#00d4ff',
    display: 'inline-block',
    boxShadow: '0 0 8px #00d4ff',
  },
  heroTitle: {
    fontSize: 42,
    fontWeight: 800,
    color: 'var(--text)',
    letterSpacing: '-0.03em',
    lineHeight: 1.1,
    margin: 0,
  },
  heroAccent: {
    color: '#7b61ff',
  },
  heroSub: {
    fontSize: 16,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
    maxWidth: 560,
    margin: 0,
  },
  heroBadges: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  badge: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    color: 'var(--text-secondary)',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    padding: '5px 12px',
  },
  divider: {
    height: 1,
    background: 'linear-gradient(90deg, transparent, var(--border), transparent)',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: '#00d4ff',
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
    margin: 0,
  },
  sectionSub: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    margin: 0,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  stepCard: {
    display: 'flex',
    gap: 20,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '24px',
    alignItems: 'flex-start',
  },
  stepLeft: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  stepNumber: {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.1em',
  },
  stepIcon: {
    width: 44,
    height: 44,
    borderRadius: 12,
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  },
  stepRight: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--text)',
    marginBottom: 8,
  },
  stepBody: {
    fontSize: 14,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
  },
  rssToggle: {
    marginTop: 12,
    background: 'transparent',
    border: 'none',
    color: '#00d4ff',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    fontFamily: 'Space Grotesk, sans-serif',
    letterSpacing: '0.04em',
  },
  rssExplainer: {
    marginTop: 12,
    padding: '16px',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 10,
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
  },
  rssCode: {
    fontFamily: 'monospace',
    color: '#00d4ff',
    background: 'rgba(0, 212, 255, 0.08)',
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: 12,
  },
  animaCard: {
    background: 'linear-gradient(135deg, rgba(123, 97, 255, 0.08), rgba(0, 212, 255, 0.05))',
    border: '1px solid rgba(123, 97, 255, 0.25)',
    borderRadius: 20,
    padding: '32px',
  },
  animaHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: 28,
  },
  animaBadge: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: '#7b61ff',
    background: 'rgba(123, 97, 255, 0.15)',
    border: '1px solid rgba(123, 97, 255, 0.3)',
    borderRadius: 20,
    padding: '3px 10px',
    alignSelf: 'flex-start',
  },
  animaTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
    margin: 0,
  },
  animaGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
  },
  animaPoint: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  animaPointIcon: {
    fontSize: 20,
    color: '#7b61ff',
  },
  animaPointTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--text)',
  },
  animaPointBody: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
  },
  table: {
    border: '1px solid var(--border)',
    borderRadius: 16,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 100px 100px',
    padding: '12px 20px',
    background: 'var(--surface)',
    borderBottom: '1px solid var(--border)',
  },
  tableCol: {},
  tableColHeader: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textAlign: 'center',
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 100px 100px',
    padding: '12px 20px',
    borderBottom: '1px solid var(--border)',
  },
  tableFeature: {
    fontSize: 13,
    color: 'var(--text-secondary)',
  },
  tableValue: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    textAlign: 'center',
  },
  ctaCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    padding: '32px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    textAlign: 'center',
  },
  ctaTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
  },
  ctaSub: {
    fontSize: 14,
    color: 'var(--text-secondary)',
  },
  ctaButtons: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 8,
  },
  ctaBtn: {
    minWidth: 160,
  },
  comingSoon: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    padding: '24px',
  },
  comingSoonTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--text-secondary)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: 16,
  },
  comingSoonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 12,
  },
  comingSoonItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: 'var(--text-secondary)',
  },
  comingSoonDot: {
    color: 'var(--text-dim)',
    fontSize: 10,
  },
};

export default AboutScreen;
